import React from 'react';
import { StockData } from '../../types';
import { cn } from '../../lib/utils';
import {
  resolveEntryStatus,
  isKnownEntryStatus,
  resolveLiveDivergence,
  buildLiveDivergenceTooltip,
} from '../../lib/deepAnalysis/entryStatusDisplay';

interface StockCardProps {
  stock: StockData;
  onPress: (ticker: string) => void;
  isSelected?: boolean;
}

/**
 * StockCard - Native-style mobile compact card component
 * Designed to look like iOS Stocks app - single line per stock
 */
export const StockCard: React.FC<StockCardProps> = ({
  stock,
  onPress,
  isSelected,
}) => {
  const isPositive = (stock.day_change_percent ?? 0) >= 0;

  const formatPrice = (price: number | undefined) => {
    if (price === undefined || price === null) return '-';
    return `$${price.toFixed(2)}`;
  };

  const formatChange = (change: number | undefined) => {
    if (change === undefined || change === null) return '-';
    const prefix = change >= 0 ? '+' : '';
    return `${prefix}${change.toFixed(2)}%`;
  };

  // Compact score color - Institutional Editor gold/emerald/crimson
  const getScoreColor = (score: number | undefined) => {
    if (score === undefined || score === null) return 'text-[#8e8e93]';
    if (score >= 7) return 'text-accent-primary'; // Gold for high scores
    if (score >= 4) return 'text-accent-bullish'; // Emerald for medium
    return 'text-accent-bearish'; // Crimson for low
  };

  // Compact signal text
  const getCompactSignal = () => {
    const rationale = stock.entry_rationale || '';
    if (rationale.includes('200DMA') || rationale.includes('MA SUPPORT'))
      return 'MA';
    if (rationale.includes('BREAKOUT')) return 'Break';
    if (rationale.includes('RSI')) return 'RSI';
    if (rationale.includes('SECOND BOTTOM')) return '2nd';
    return '';
  };

  const compactSignal = getCompactSignal();

  // Entry Status (buy-readiness) badge - uses the shared SSOT helper
  // (entryStatusDisplay.ts) so label/emoji/tone never drift from the desktop
  // table + deep-chart pill. docs/SPEC_ENTRY_STATUS_2026-06-18.md §3.
  const entryStatusBadge = isKnownEntryStatus(stock.entry_status)
    ? (() => {
        const d = resolveEntryStatus(stock.entry_status);
        // Prefer the backend's plain-language entry_instruction as the tooltip
        // (SPEC_ENTRY_CONVICTION_2026-06-19); fall back to the badge label.
        const instruction =
          typeof stock.entry_instruction === 'string' && stock.entry_instruction.trim()
            ? stock.entry_instruction
            : d.label;
        // Live-divergence hint (PLAN_ENTRY_STATUS_PARITY_2026-06-22) - same shared
        // helpers as the desktop table/badge/pill so mobile reaches parity:
        // "screened Buy, live Holding". Not i18n'd here (label is the SSOT
        // English), matching the card's existing non-translated entry label.
        const { show, liveDisplay } = resolveLiveDivergence(
          stock.entry_status,
          stock.live_entry_status,
        );
        return {
          label: d.label,
          toneClass: d.textClass,
          emoji: d.emoji,
          title:
            show && liveDisplay
              ? buildLiveDivergenceTooltip(instruction, 'Live', liveDisplay.label)
              : instruction,
          liveEmoji: show && liveDisplay ? liveDisplay.emoji || '•' : null,
        };
      })()
    : null;

  return (
    <div
      className={cn(
        // Institutional Editor: tonal card, no-line design
        'mx-3 my-1 rounded-xl',
        'bg-surface-container',
        'cursor-pointer',
        'transition-all duration-150',
        'active:bg-surface-container-high',
        isSelected && 'ring-1 ring-accent-primary'
      )}
      onClick={() => onPress(stock.ticker)}
    >
      {/* Single compact row - all in one line */}
      <div className="flex items-center justify-between px-4 py-3.5">
        {/* Left: Ticker + Company */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-display font-semibold text-on-surface text-sm">
              {stock.ticker}
            </span>
            {stock.score !== undefined && stock.score !== null && (
              <span
                className={cn(
                  'text-xs font-bold',
                  getScoreColor(stock.score)
                )}
              >
                {stock.score.toFixed(0)}
              </span>
            )}
            {compactSignal && (
              <span className="text-xs bg-surface-container-high text-on-surface-variant px-1.5 py-0.5 rounded">
                {compactSignal}
              </span>
            )}
            {entryStatusBadge && (
              <span
                className={cn(
                  'text-xs font-semibold bg-surface-container-high px-1.5 py-0.5 rounded',
                  entryStatusBadge.toneClass
                )}
                title={entryStatusBadge.title}
              >
                {entryStatusBadge.emoji} {entryStatusBadge.label}
                {entryStatusBadge.liveEmoji && (
                  <span aria-hidden className="opacity-60"> {entryStatusBadge.liveEmoji}</span>
                )}
              </span>
            )}
          </div>
          <div className="text-on-surface-variant text-xs truncate max-w-[160px]">
            {stock.company_name}
          </div>
        </div>

        {/* Right: Price + Change */}
        <div className="text-right flex-shrink-0 ml-3">
          <div className="font-medium text-on-surface text-sm">
            {formatPrice(stock.current_price)}
          </div>
          <div
            className={cn(
              'text-xs font-medium',
              isPositive ? 'text-accent-bullish' : 'text-accent-bearish'
            )}
          >
            {formatChange(stock.day_change_percent)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockCard;
